import { gql, useQuery } from '@material-bank/graphql-client';

// The full product selection, shared so the standalone `productByVariantSkus`
// query and the merged `Tag.product` field (useTagQuery) can never drift.
export const PRODUCT_FIELDS = gql`
  fragment ProductFields on ProductByVariantSkusResult {
    sku
    name
    brandRef
    slug
    masterVariantSKU
    variantSKUs
    categories {
      key
      name
      slug
      categoryId
      parentCategoryId
    }
    topAttributes {
      name
      value
    }
    images {
      url
      label
    }
    masterVariant {
      sku
      brandName
      colorName
      colorFamily
      images {
        url
        label
      }
      attributes {
        name
        value
      }
      documents {
        key
        name
        uri
        contentType
      }
    }
    variants {
      sku
      productSKU
      colorName
      colorFamily
      brandName
      images {
        url
        label
      }
      attributes {
        name
        value
      }
      documents {
        key
        name
        uri
        contentType
      }
    }
  }
`;
export const PRODUCT_BY_VARIANT_SKUS_QUERY = gql`
  query ProductByVariantSkus($skus: [String!]!, $locale: String) {
    productByVariantSkus(skus: $skus, locale: $locale) {
      ...ProductFields
    }
  }
  ${PRODUCT_FIELDS}
`;
export type ProductByVariantSkusImage = {
  url: string;
  label?: string | null;
};
export type ProductByVariantSkusCategory = {
  key: string;
  name?: string | null;
  slug?: string | null;
  categoryId?: string | null;
  parentCategoryId?: string | null;
};
export type ProductByVariantSkusTopAttribute = {
  name: string;
  value: string;
};
export type ProductByVariantSkusAttribute = {
  name: string;
  value?: string | null;
};

// Mirrors the `documents` shape produced by the BFF mapper
// (mtag-bff: productByVariantSkusMapper.ts) and the GraphQL SDL. Duplicated
// rather than imported to avoid a client → BFF dependency; keep the two in
// sync if the GraphQL contract changes.
export type ProductByVariantSkusDocument = {
  key: string;
  name: string;
  uri: string;
  contentType: string;
};
export type ProductByVariantSkusVariant = {
  sku?: string | null;
  productSKU?: string | null;
  colorName?: string | null;
  colorFamily?: string | null;
  brandName?: string | null;
  images: ProductByVariantSkusImage[];
  attributes?: ProductByVariantSkusAttribute[];
  documents?: ProductByVariantSkusDocument[];
};
export type ProductByVariantSkusResult = {
  sku: string;
  name: string;
  brandRef?: string | null;
  slug?: string | null;
  masterVariantSKU?: string | null;
  variantSKUs: string[];
  categories: ProductByVariantSkusCategory[];
  topAttributes: ProductByVariantSkusTopAttribute[];
  images: ProductByVariantSkusImage[];
  masterVariant?: ProductByVariantSkusVariant | null;
  variants: ProductByVariantSkusVariant[];
};
type ProductByVariantSkusQueryData = {
  productByVariantSkus: ProductByVariantSkusResult | null;
};
type ProductByVariantSkusQueryVars = {
  skus: string[];
  locale?: string | null;
};
export function useProductByVariantSkusQuery(options) {
  return useQuery(PRODUCT_BY_VARIANT_SKUS_QUERY, options);
}